import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Zap } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const PayoutsManager = () => {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("e-transfer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [auto, setAuto] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/payouts").then((r) => setData(r.data));
    api.get("/admin/refunds").then((r) => setRefunds(r.data)).catch(() => {});
    api.get("/admin/connect/overview").then((r) => setAuto(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runPayouts = async () => {
    setRunning(true);
    try {
      const { data: run } = await api.post("/admin/payouts/run");
      if (run.status === "already_running") toast.info("A payout run is already in progress");
      else toast.success(`Payout run complete — $${run.paid_total} CAD sent in ${run.transfers} transfer${run.transfers !== 1 ? "s" : ""}${run.failures?.length ? ` · ${run.failures.length} failed` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setRunning(false); }
  };

  const connectInfo = (id) => auto?.practitioners?.find((p) => p.practitioner_id === id);
  const connectChip = (info) => {
    if (!info || !info.connected) return ["Manual (e-transfer)", "text-muted-foreground border-border"];
    if (info.ready) return ["Auto payouts on", "text-primary border-primary/30"];
    return ["Stripe setup pending", "text-foreground/80 border-foreground/30"];
  };

  const markRefunded = async (b) => {
    try {
      const r = await api.post(`/admin/refunds/${b.id}/complete`);
      toast.success(r.data.stripe_refund
        ? `Refund of $${b.refund_amount} issued via Stripe — ${b.seeker_name} has been emailed`
        : `Refund of $${b.refund_amount} marked complete — ${b.seeker_name} has been emailed`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDialog = (row) => {
    setTarget(row);
    setAmount(String(row.balance > 0 ? row.balance : ""));
    setMethod("e-transfer");
    setNote("");
  };

  const record = async () => {
    setBusy(true);
    try {
      await api.post("/admin/payouts", { practitioner_id: target.practitioner_id, amount: Number(amount), method, note });
      toast.success(`Payout of $${amount} recorded for ${target.business_name}`);
      setTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="admin-payouts-manager">
      {refunds.length > 0 && (
        <div className="mb-10" data-testid="pending-refunds">
          <h2 className="font-display text-lg md:text-2xl text-foreground mb-2">Pending refunds</h2>
          <p className="text-sm text-muted-foreground mb-4">Click "Refund via Stripe" to send the money back to the seeker's card — they'll get a confirmation email automatically.</p>
          <div className="space-y-2">
            {refunds.map((b) => (
              <div key={b.id} className="silk-card rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3 border-destructive/30" data-testid={`refund-row-${b.id}`}>
                <div className="text-sm">
                  <p className="text-foreground">${b.refund_amount} CAD → {b.seeker_name} <span className="text-muted-foreground">({b.seeker_email})</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">{b.service?.name} with {b.business_name} · {b.date} {b.time} · {b.refund_reason}</p>
                </div>
                <Button size="sm" data-testid={`mark-refunded-${b.id}`} onClick={() => markRefunded(b)} className="rounded-full px-4">Refund via Stripe</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <h2 className="font-display text-lg md:text-2xl text-foreground mb-2">Practitioner payouts</h2>
      <p className="text-sm text-muted-foreground mb-4">Practitioners who've connected Stripe receive their 90% share automatically on the 1st and 15th for sessions that have taken place. Everyone else stays on the manual ledger — record e-transfers here.</p>

      <div className="silk-card rounded-lg px-4 py-3 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="auto-payouts-panel">
        <div className="text-sm">
          <p className="text-foreground">Automatic Stripe payouts</p>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="auto-payouts-summary">
            {auto?.next_run ? `Next scheduled run ${new Date(auto.next_run).toLocaleDateString("en-CA", { month: "long", day: "numeric" })}` : "Schedule: 1st and 15th"}
            {auto?.runs?.[0] ? ` · Last run ${new Date(auto.runs[0].started_at).toLocaleDateString("en-CA")}: $${auto.runs[0].paid_total} CAD in ${auto.runs[0].transfers} transfer${auto.runs[0].transfers !== 1 ? "s" : ""}${auto.runs[0].failures?.length ? `, ${auto.runs[0].failures.length} failed` : ""}` : " · No automatic runs yet"}
          </p>
        </div>
        <Button size="sm" className="rounded-full px-5" data-testid="run-payouts-btn" disabled={running} onClick={runPayouts}>
          <Zap size={13} className="mr-1.5" />{running ? "Running…" : "Run payouts now"}
        </Button>
      </div>

      <div className="space-y-2 mb-10" data-testid="payouts-summary">
        {data.summary.map((row) => {
          const info = connectInfo(row.practitioner_id);
          const [chipLabel, chipClass] = connectChip(info);
          return (
          <div key={row.practitioner_id} className="silk-card rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`payout-row-${row.practitioner_id}`}>
            <div className="min-w-0">
              <p className="text-sm text-foreground truncate">{row.business_name}
                <span className={`ml-2 text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${chipClass}`} data-testid={`connect-chip-${row.practitioner_id}`}>{chipLabel}</span>
              </p>
              <p className="text-xs text-muted-foreground">Earned ${row.net_earned} · Paid out ${row.paid_out}{info?.ready && info.eligible_count > 0 ? ` · $${info.eligible_now} ready for next auto run` : ""}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`text-sm ${row.balance > 0 ? "text-primary" : "text-muted-foreground"}`} data-testid={`payout-balance-${row.practitioner_id}`}>
                Owing ${row.balance}
              </span>
              <Button size="sm" variant="outline" className="rounded-full" disabled={row.balance <= 0}
                data-testid={`record-payout-${row.practitioner_id}`} onClick={() => openDialog(row)}>
                <Banknote size={13} className="mr-1.5" /> Record payout
              </Button>
            </div>
          </div>
          );
        })}
        {data.summary.length === 0 && <p className="text-sm text-muted-foreground">No approved practitioners yet.</p>}
      </div>

      <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Recent payouts</h3>
      <div className="space-y-2" data-testid="recent-payouts">
        {data.recent.map((p) => (
          <div key={p.id} className="silk-card rounded-lg px-4 py-2.5 flex items-center justify-between text-sm">
            <span className="text-foreground">{p.business_name} — ${p.amount} CAD</span>
            <span className="text-xs text-muted-foreground">{p.method === "stripe" ? "Stripe transfer" : p.method}{p.note ? ` · ${p.note}` : ""} · {new Date(p.created_at).toLocaleDateString("en-CA")}</span>
          </div>
        ))}
        {data.recent.length === 0 && <p className="text-sm text-muted-foreground">No payouts recorded yet.</p>}
      </div>

      <Dialog open={!!target} onOpenChange={(v) => { if (!v) setTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Record payout</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-2">{target.business_name} — outstanding balance <span className="text-primary">${target.balance} CAD</span></p>
              <Input data-testid="payout-amount-input" type="number" min="0.01" step="0.01" placeholder="Amount (CAD)" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Input data-testid="payout-method-input" placeholder="Method (e-transfer, cheque…)" value={method} onChange={(e) => setMethod(e.target.value)} />
              <Input data-testid="payout-note-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button data-testid="payout-save-btn" className="rounded-full px-6" disabled={busy || !amount || Number(amount) <= 0} onClick={record}>
                {busy ? "Recording…" : "Record payout"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
